// components/AppUsageChart.tsx
// App usage metrics visualization

import React from 'react';
import type { AppUsageStats } from '../types';
import './AppUsageChart.css';

interface Props {
    data: AppUsageStats[];
}

export const AppUsageChart: React.FC<Props> = ({ data }) => {
    if (data.length === 0) {
        return (
            <div className="app-usage-chart">
                <p className="empty-state">No data available for this date range</p>
            </div>
        );
    }

    // Calculate totals
    const totals = data.reduce(
        (acc, day) => ({
            launches: acc.launches + day.totalLaunches,
            users: Math.max(acc.users, day.uniqueUsers),
            sessions: acc.sessions + day.totalSessions,
            crashes: acc.crashes + day.crashCount,
            errors: acc.errors + day.errorCount,
        }),
        { launches: 0, users: 0, sessions: 0, crashes: 0, errors: 0 }
    );

    // Calculate averages
    const avgSessionDuration = data.reduce((sum, day) => sum + day.avgSessionDuration, 0) / data.length;

    return (
        <div className="app-usage-chart">
            {/* Summary Cards */}
            <div className="summary-cards">
                <div className="metric-card">
                    <h4>Total Launches</h4>
                    <p className="metric-value">{totals.launches.toLocaleString()}</p>
                </div>
                <div className="metric-card">
                    <h4>Unique Users</h4>
                    <p className="metric-value">{totals.users.toLocaleString()}</p>
                </div>
                <div className="metric-card">
                    <h4>Total Sessions</h4>
                    <p className="metric-value">{totals.sessions.toLocaleString()}</p>
                </div>
                <div className="metric-card">
                    <h4>Avg Session</h4>
                    <p className="metric-value">
                        {Math.round(avgSessionDuration / 60)}m
                    </p>
                    <p className="metric-subtitle">{Math.round(avgSessionDuration)}s</p>
                </div>
            </div>

            {/* Error & Crash Stats */}
            <div className="error-stats">
                <div className="error-stat">
                    <span className="error-label">Crashes:</span>
                    <span className={totals.crashes > 0 ? 'error-count warning' : 'error-count'}>
                        {totals.crashes}
                    </span>
                </div>
                <div className="error-stat">
                    <span className="error-label">Errors:</span>
                    <span className={totals.errors > 0 ? 'error-count warning' : 'error-count'}>
                        {totals.errors}
                    </span>
                </div>
            </div>

            {/* Daily breakdown */}
            <div className="daily-breakdown">
                <h4>Daily Breakdown</h4>
                <div className="breakdown-list">
                    {[...data].reverse().map((day) => (
                        <div key={day.date} className="breakdown-item">
                            <span className="breakdown-date">{day.date}</span>
                            <div className="breakdown-metrics">
                                <span className="breakdown-metric">
                                    {day.totalLaunches} launches
                                </span>
                                <span className="breakdown-metric">
                                    {day.uniqueUsers} users
                                </span>
                                {day.crashCount > 0 && (
                                    <span className="breakdown-metric error">
                                        {day.crashCount} crashes
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default AppUsageChart;
